import { all, call, delay, put, select } from "redux-saga/effects";
import dayjs from "dayjs";
import {
  ActivitiesSelectionEnum,
  IdEnum,
  IResult,
  FlightItineraryResponse,
  FlightItineraryState,
  HotelItineraryResponse,
  HotelItineraryState,
  BookedFlightItineraryWithDepartureTime,
  HotelItinerary,
  Airport,
} from "redmond";

import { actions } from "../actions";
import { TripDestination, CombinedAutocompleteResponse } from "../types";
import { getTripDestinationsLoaded } from "../reducer";
import {
  getMockTripDestinations,
  MockTripScenario,
} from "../mocks/tripDestinationsMocks";
import { fetchFlightItineraries } from "../api/fetchFlightItineraries";
import { fetchHotelItineraries } from "../api/fetchHotelItineraries";
import { fetchCombinedAutocomplete } from "../../../api/v0/search/fetchCombinedAutocomplete";
import Logger from "../../../utils/logger";

// Default trip duration for one-way flights without a return booking
const DEFAULT_TRIP_DURATION_DAYS = 3;

// Maximum number of upcoming destinations to display
const MAX_DISPLAYED_DESTINATIONS = 3;

interface RawDestination {
  lat: number;
  lon: number;
  displayName: string;
  tripDate: string;
  tripEndDate: string;
  source: "Flight" | "Hotel";
}

function extractFlightDestinations(
  response: FlightItineraryResponse
): RawDestination[] {
  const airports: { [key: string]: Airport } = response.airports || {};

  // Combine present and future itineraries
  const allItineraries: BookedFlightItineraryWithDepartureTime[] = [
    ...(response.itineraries?.present || []),
    ...(response.itineraries?.future || []),
  ];

  return allItineraries
    .map((itinerary) => {
      const slices = itinerary.bookedItinerary?.travelItinerary?.slices || [];
      if (slices.length === 0) return null;

      // Get the destination from the first slice (outbound flight)
      const outboundSlice = slices[0];
      const segments = outboundSlice?.segments || [];
      if (segments.length === 0) return null;

      // Last segment of outbound gives us the destination
      const lastSegment = segments[segments.length - 1];
      const destinationCode = lastSegment?.destination?.locationCode;
      if (!destinationCode) return null;

      const airport = airports[destinationCode];
      if (!airport?.coordinates) return null;

      // Calculate trip dates - use zonedUpdatedArrival or fall back to updatedArrival
      const arrivalTime = lastSegment.zonedUpdatedArrival || lastSegment.updatedArrival;
      const tripDate = arrivalTime?.split("T")[0];
      if (!tripDate) return null;

      let tripEndDate: string;
      if (slices.length > 1) {
        // Round-trip: use return arrival time
        const returnSlice = slices[slices.length - 1];
        const returnSegments = returnSlice?.segments || [];
        const lastReturnSegment = returnSegments[returnSegments.length - 1];
        const returnArrival = lastReturnSegment?.zonedUpdatedArrival || lastReturnSegment?.updatedArrival;
        tripEndDate = returnArrival?.split("T")[0] || tripDate;
      } else {
        // One-way: use default trip length
        tripEndDate = dayjs(tripDate).add(DEFAULT_TRIP_DURATION_DAYS, "day").format("YYYY-MM-DD");
      }

      return {
        lat: airport.coordinates.lat,
        lon: airport.coordinates.lon,
        displayName: airport.cityName || airport.name,
        tripDate,
        tripEndDate: tripEndDate || tripDate,
        source: "Flight",
      };
    })
    .filter((d) => d != null) as RawDestination[];
}

function extractHotelDestinations(
  response: HotelItineraryResponse
): RawDestination[] {
  // Combine present and future itineraries
  const allItineraries: HotelItinerary[] = [
    ...(response.itineraries?.present || []),
    ...(response.itineraries?.future || []),
  ];

  return allItineraries
    .map((hotelItinerary) => {
      const {reservation} = hotelItinerary;
      if (!reservation) return null;

      const coords = reservation.lodgingData?.location?.coordinates;
      if (!coords?.lat || !coords?.lon) return null;

      const {checkInDate} = reservation;
      const {checkOutDate} = reservation;
      const city = reservation.lodgingData?.city;

      if (!checkInDate || !checkOutDate || !city) return null;

      return {
        lat: coords.lat,
        lon: coords.lon,
        displayName: city,
        tripDate: checkInDate,
        tripEndDate: checkOutDate,
        source: "Hotel",
      };
    })
    .filter((d) => d != null) as RawDestination[];
}

interface GroupType {
  CombinedSearchResultType?: string;
}

interface CombinedGroup {
  groupType: GroupType | string;
  suggestions?: Array<{
    label: string;
    id: string;
    destinationId?: string;
    destinationName?: string;
    thumbnailUrl?: string;
    trackingPropertiesV2?: Record<string, unknown>;
  }>;
}

function* resolveLocationForDestination(
  rawDest: RawDestination
): Generator<unknown, TripDestination | null, unknown> {
  try {
    const response = (yield call(fetchCombinedAutocomplete, {
      query: rawDest.displayName,
    })) as CombinedAutocompleteResponse;

    // Accept both Destination and Category groups since city searches may return categories
    // groupType is an object like {CombinedSearchResultType: "Destination"}
    const groups = response?.groups as CombinedGroup[] | undefined;
    const destGroup = groups?.find(
      (g: CombinedGroup) => {
        const typeStr = typeof g.groupType === "object"
          ? g.groupType?.CombinedSearchResultType
          : g.groupType;
        return typeStr === "Destination" || typeStr === "Category";
      }
    );
    const firstDest = destGroup?.suggestions?.[0];

    if (!firstDest) {
      return null;
    }

    // For category results, use the city name from the raw destination
    const groupTypeStr = typeof destGroup.groupType === "object"
      ? destGroup.groupType?.CombinedSearchResultType
      : destGroup.groupType;
    const isCategory = groupTypeStr === "Category";

    const destinationId = isCategory
      ? firstDest.destinationId || rawDest.displayName
      : firstDest.destinationId || firstDest.id;
    const destinationName = isCategory
      ? rawDest.displayName
      : firstDest.destinationName || firstDest.label;

    // Build IResult from combined autocomplete result
    const location: IResult = {
      label: isCategory ? rawDest.displayName : firstDest.label,
      id: {
        Id: IdEnum.Activities,
        activitiesSelection: {
          ActivitiesSelection: ActivitiesSelectionEnum.ActivitiesDestination,
          id: destinationId,
          name: destinationName,
          searchTerm: isCategory ? rawDest.displayName : firstDest.label,
        },
      },
      trackingPropertiesV2: firstDest.trackingPropertiesV2 || {},
    };

    return {
      location,
      tripDate: rawDest.tripDate,
      tripEndDate: rawDest.tripEndDate,
      source: rawDest.source,
      coordinates: { lat: rawDest.lat, lon: rawDest.lon },
      displayName: rawDest.displayName,
      thumbnailUrl: firstDest.thumbnailUrl,
    };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    Logger.debug(`Failed to resolve location for ${rawDest.displayName}: ${message}`);
    return null;
  }
}

export function* fetchTripDestinationsSaga() {
  try {
    // Check for mock data URL parameter (e.g., ?mockTrips=none|mix|ongoing&mockTripsDelay=3000)
    // Only enabled in non-production environments for QA testing
    const urlParams = new URLSearchParams(window.location.search);
    const mockScenario = urlParams.get("mockTrips") as MockTripScenario | null;
    const mockDelay = parseInt(urlParams.get("mockTripsDelay") || "0", 10);
    if (process.env.NODE_ENV !== "production" && mockScenario) {
      const mockData = getMockTripDestinations(mockScenario);
      if (mockData !== null) {
        Logger.debug(`Using mock trip destinations: ${mockScenario}`);
        if (mockDelay > 0) {
          Logger.debug(`Simulating ${mockDelay}ms load delay`);
          yield delay(mockDelay);
        }
        yield put(actions.setTripDestinations(mockData));
        return;
      }
    }

    // Check if already loaded
    const loaded: boolean = yield select(getTripDestinationsLoaded);
    if (loaded) {
      // Reset loading state since FETCH_TRIP_DESTINATIONS action sets it to true
      yield put(actions.setTripDestinationsLoading(false));
      return;
    }

    const referenceDateTime = dayjs().toISOString();
    const flightRequestParams = {
      states: [FlightItineraryState.Present, FlightItineraryState.Future],
      referenceDateTime,
    };
    const hotelRequestParams = {
      states: [HotelItineraryState.Present, HotelItineraryState.Future],
      referenceDateTime,
    };

    // Fetch flights and hotels in parallel
    let flightResponse: FlightItineraryResponse = { itineraries: {}, airports: {}, airlines: {} };
    let hotelResponse: HotelItineraryResponse = { itineraries: {} };

    try {
      const [flights, hotels]: [FlightItineraryResponse, HotelItineraryResponse] =
        yield all([
          call(fetchFlightItineraries, flightRequestParams),
          call(fetchHotelItineraries, hotelRequestParams),
        ]);
      flightResponse = flights;
      hotelResponse = hotels;
    } catch (error: unknown) {
      // Try to fetch each independently if parallel fails
      const message = error instanceof Error ? error.message : String(error);
      Logger.debug(`Parallel fetch failed, trying individually: ${message}`);
      try {
        flightResponse = yield call(fetchFlightItineraries, flightRequestParams);
      } catch (flightError: unknown) {
        const flightMsg = flightError instanceof Error ? flightError.message : String(flightError);
        Logger.debug(`Failed to fetch flight itineraries: ${flightMsg}`);
      }
      try {
        hotelResponse = yield call(fetchHotelItineraries, hotelRequestParams);
      } catch (hotelError: unknown) {
        const hotelMsg = hotelError instanceof Error ? hotelError.message : String(hotelError);
        Logger.debug(`Failed to fetch hotel itineraries: ${hotelMsg}`);
      }
    }
    Logger.debug(`Fetched ${flightResponse.itineraries?.present?.length || 0} present, ${flightResponse.itineraries?.future?.length || 0} future flights`);
    Logger.debug(`Fetched ${hotelResponse.itineraries?.present?.length || 0} present, ${hotelResponse.itineraries?.future?.length || 0} future hotels`);

    // Extract destinations from responses
    const flightDestinations = extractFlightDestinations(flightResponse);
    const hotelDestinations = extractHotelDestinations(hotelResponse);
    Logger.debug(`Extracted ${flightDestinations.length} flight destinations`);
    Logger.debug(`Extracted ${hotelDestinations.length} hotel destinations`);

    // Combine and sort by trip date, take first 3 upcoming
    const allRawDestinations = [...flightDestinations, ...hotelDestinations]
      .filter((d) => dayjs(d.tripDate).isAfter(dayjs().subtract(1, "day")))
      .sort((a, b) => dayjs(a.tripDate).diff(dayjs(b.tripDate)))
      .slice(0, MAX_DISPLAYED_DESTINATIONS);

    if (allRawDestinations.length === 0) {
      yield put(actions.setTripDestinations([]));
      return;
    }

    // Resolve each destination to an activity location
    const resolvedDestinations: (TripDestination | null)[] = yield all(
      allRawDestinations.map((dest) => call(resolveLocationForDestination, dest))
    );

    const validDestinations = resolvedDestinations.filter(
      (d): d is TripDestination => d !== null
    );

    yield put(actions.setTripDestinations(validDestinations));
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    Logger.debug(`Failed to fetch trip destinations: ${message}`);
    yield put(
      actions.setTripDestinationsError("Failed to fetch trip destinations")
    );
  }
}

